import { Injectable, inject } from '@angular/core';
import { Actions, createEffect } from '@ngrx/effects';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { PermissionsService } from '../../services/permissions.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { ofType } from '@ngrx/effects';
import { PermissionsActions } from '../actions/actions-type.action';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { SnackBarType } from 'src/app/shared/components/snack-bar/snack-bar.component';
import { FinalizedPermissionsResponse } from '../../models/responses/finalized-permissions.response';
import { PermissionDetailed } from '../../models/responses/permissions.response';
import { PermissionGroup } from '../../models/requests/finalized-permissions.request';


@Injectable()
export class PermissionsEffect {
    private actions$ = inject(Actions);
    private notificationsService = inject(NotificationsService);
    private permissionsService = inject(PermissionsService);

    fetchPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsActions.fetchPermissionsRequests),
            switchMap((action) => {
                return this.permissionsService
                    .fetchPermissionsRequest(action.payload)
                    .pipe(
                        map((response: FinalizedPermissionsResponse) =>
                            PermissionsActions.fetchPermissionsRequestsSuccess(
                                {
                                    payload: response
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                            });

                            return of(
                                PermissionsActions.fetchPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchTournamentPermissionsRequest$ = createEffect(() =>
            this.actions$.pipe(
                ofType(PermissionsActions.fetchTournamentPermissionsRequest),
                switchMap((action) => {
                    return this.permissionsService
                        .fetchPermissionsRequest(action.payload.userId, action.payload.permissionContexts)
                        .pipe(
                            map((response: FinalizedPermissionsResponse) =>
                                PermissionsActions.fetchTournamentPermissionsRequestsSuccess(
                                    {
                                        payload: response
                                    }
                                )
                            ),
                            catchError((response: BadResponseWithError) => {
                                this.notificationsService.openSnackBar({
                                    title: response?.error?.message,
                                    type: SnackBarType.Error,
                                });

                                return of(
                                    PermissionsActions.fetchTournamentPermissionsRequestsFailure(
                                        {
                                            payload: response,
                                        }
                                    )
                                );
                            })
                        );
                })
            )
    );

    fetchTournamentEntitiesPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsActions.fetchTournamentEntitiesPermissionsRequest),
            switchMap((action) => {
                return this.permissionsService
                    .fetchPermissionRequest(action.payload.userId, action.payload.permissionContexts)
                    .pipe(
                        map((response: any) =>
                            PermissionsActions.fetchTournamentEntitiesPermissionsRequestsSuccess(
                                {
                                    payload: response
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                            });

                            return of(
                                PermissionsActions.fetchTournamentEntitiesPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );


    fetchDetailedPermissionsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsActions.fetchPermissionsRequests),
            switchMap((action) => {
                return this.permissionsService
                    .fetchDetailedPermissionsRequest()
                    .pipe(
                        map((response: PermissionDetailed[]) =>
                            PermissionsActions.fetchDetailedPermissionsRequestsSuccess(
                                {
                                    payload: response
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            return of(
                                PermissionsActions.fetchPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );

    fetchGroupRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PermissionsActions.fetchTournamentGroup),
            switchMap((action) => {
                return this.permissionsService
                    .fetchGroup(action.payload.name, action.payload.contextEntityType, action.payload.contextEntityId)
                    .pipe(map((response: PermissionGroup) =>
                        {
                            response.searchName = action.payload.name;
                            return PermissionsActions.fetchTournamentGroupSuccess(
                                {
                                    payload: response
                                }
                            )
                        }
                        ),
                        catchError((response: BadResponseWithError) => {
                            return of(
                                PermissionsActions.fetchTournamentGroupFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
}

